import React, { useState, useEffect } from "react";
import {
  Drawer,
  Typography,
  Button,
  Input,
  Select,
  Space,
  Flex,
  Spin,
  Empty,
} from "antd";
import { SettingOutlined, CloseOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { ATSJobImportDrawerProps } from "../../../types/Step0.types";
import atsIntegrationService from "../../../../../Service/ats.integration.service";
import ATSJobPostCard from "./ATSJobPostCard";
import { ATSJobPost } from "../../../../../Interface";
import "./index.scss";
import { useAuth } from "../../../../../Context/AuthContext/AuthContext";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const ATSJobImportDrawer: React.FC<ATSJobImportDrawerProps> = ({
  visible,
  onClose,
  provider,
  appName,
  onImport,
}) => {
  const navigate = useNavigate();
  const [jobPosts, setJobPosts] = useState<ATSJobPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState<"title" | undefined>(undefined);
  const [drawerWidth, setDrawerWidth] = useState<string | number>("50%");
  const displayName = appName || provider.label;

  const { user } = useAuth();

  // Handle responsive width
  useEffect(() => {
    const updateWidth = () => {
      setDrawerWidth(window.innerWidth < 768 ? "80%" : "50%");
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const fetchJobPosts = React.useCallback(async () => {
    setLoading(true);
    try {
      const appType = provider.key;
      const response = await atsIntegrationService.getATSJobPosts(
        appType,
        searchValue || undefined,
        sortBy
      );
      if (response.success && response.data) {
        setJobPosts(response.data);
      } else {
        console.error("Error fetching job posts:", response.message);
        setJobPosts([]);
      }
    } catch (error) {
      console.error("Error fetching job posts:", error);
      setJobPosts([]);
    } finally {
      setLoading(false);
    }
  }, [provider.key, searchValue, sortBy]);

  useEffect(() => {
    if (visible) {
      fetchJobPosts();
    } else {
      setSearchValue("");
      setSortBy(undefined);
      setJobPosts([]);
    }
  }, [visible, provider, fetchJobPosts]);

  const handleSettingsClick = () => {
    navigate("/organization?tab=apps");
  };

  const handleImport = (promptText: string, appJobId: string) => {
    const appType = provider.key; // Get app type from provider (e.g., 'lever', 'zoho')
    onImport(promptText, appJobId, appType);
    onClose();
  };

  return (
    <Drawer
      title={null}
      placement="right"
      onClose={onClose}
      open={visible}
      width={drawerWidth}
      closable={false}
      className="ats-job-import-drawer"
      styles={{
        body: {
          padding: 24,
        },
      }}
    >
      <div className="ats-job-import-drawer-content">
        {/* Header Row */}
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: 24 }}
        >
          <Title level={4} style={{ margin: 0 }}>
            {displayName}
          </Title>
          <Space>
            {user && user.role === "admin" && (
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={handleSettingsClick}
                aria-label="Settings"
              >
                Settings
              </Button>
            )}
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onClose}
              aria-label="Close"
            />
          </Space>
        </Flex>

        {/* Search/Sort Row */}
        <Space
          direction="vertical"
          size="middle"
          style={{ width: "100%", marginBottom: 24 }}
        >
          <Search
            placeholder="Search by title or description"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            allowClear
            style={{ width: "100%" }}
          />
          <Select
            value={sortBy}
            onChange={setSortBy}
            style={{ width: "100%" }}
            placeholder="Sort by"
          >
            <Option value="title">Sort by Title</Option>
          </Select>
        </Space>

        {/* Content Area */}
        <div className="ats-job-import-drawer-list">
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Spin size="large" />
            </div>
          ) : jobPosts.length > 0 ? (
            jobPosts.map((jobPost) => (
              <ATSJobPostCard
                key={jobPost.jobPostId}
                jobPost={jobPost}
                onImport={handleImport}
              />
            ))
          ) : (
            <Empty
              description="No job posts available"
              style={{ marginTop: 40 }}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default ATSJobImportDrawer;
